import React, { useState, useEffect } from "react";
import { Vehicle } from "../models";
import vehiclesService, { EcmOdometer } from "services/vehiclesService";
import leaseCompaniesService, { LeaseCompany } from "services/leaseCompaniesService";
import {
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import { Hub, KeyValue } from "../../common/models";
import orgDataService from "services/orgDataService";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import HistoryIcon from "@mui/icons-material/History";
import VehicleTrips from "./VehicleTrips";
import VehicleHistory from "./VehicleHistory";

const VehicleEdit = () => {
  const [vehicle, setVehicle] = useState<Vehicle>();
  const [hubs, setHubs] = useState<Hub[]>();
  const [types, setTypes] = useState<KeyValue[]>();
  const [statuses, setStatuses] = useState<KeyValue[]>();
  const [outOfServiceReasons, setOutOfServiceReasons] = useState<KeyValue[]>();
  const [leaseCompanies, setLeaseCompanies] = useState<LeaseCompany[]>([]);
  const [displayOutOfServiceReason, setDisplayOutOfServiceReason] = useState<boolean>(false);
  const [ecmOdometer, setEcmOdometer] = useState<EcmOdometer | null>(null);
  const { id } = useParams();
  const [trips, setTrips] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    orgDataService.getAllHubs().then((data) => setHubs(data));
    orgDataService.getVehicleTypes().then((data) => setTypes(data));
    orgDataService.getVehicleStatuses().then((data) => setStatuses(data));
    orgDataService.getOutOfServiceReasons().then((data) => setOutOfServiceReasons(data));
    leaseCompaniesService.getLeaseCompanies(true).then((data) => setLeaseCompanies(data));
    vehiclesService.getVehicle(+id).then((data) => setVehicle(data));
  }, []);

  useEffect(() => {
    if (!vehicle?.unitNumber) return;
    vehiclesService.getEcmOdometer(vehicle.unitNumber).then((data) => setEcmOdometer(data));
  }, [vehicle?.unitNumber]);

  useEffect(() => {
    if (!(vehicle && (statuses?.length ?? 0) > 0 && (outOfServiceReasons?.length ?? 0) > 0)) return;
    let outOfStatusId = statuses?.find((s) => s.name === "Out Of Service");
    setDisplayOutOfServiceReason(vehicle.statusId == outOfStatusId?.id.toString());
  }, [vehicle?.statusId, statuses, outOfServiceReasons]);
  
  const save = () => {
    if (!vehicle) return;

    vehiclesService.saveVehicle(vehicle);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      {vehicle && (
        <>
          <Card>
            <MDBox p={2} xs={3}>
              <MDTypography variant="h5">
                Vehicle Info{" "}
                <Link title="View History" href={`/maps/history?unitNumber=${vehicle.unitNumber}`}>
                  <HistoryIcon />
                </Link>
              </MDTypography>
            </MDBox>
            <MDBox pb={2} px={2}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="body2" color="text">
                    <b>Description:</b> {vehicle.description}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Unit Number:</b> {vehicle.unitNumber}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Plate Number:</b> {vehicle.plateNumber}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>VIN:</b> {vehicle.vin}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    <b>Odometer:</b> {vehicle.odometer}
                  </MDTypography>
                  {ecmOdometer?.odometerValue != null && (
                    <MDTypography variant="body2" color="text">
                      <b>Last ECM Odometer:</b> {Number(ecmOdometer.odometerValue).toLocaleString()}
                      {ecmOdometer.odometerDateTimeUTC && (
                        <> &nbsp;<span style={{ color: "#888" }}>
                          ({new Date(ecmOdometer.odometerDateTimeUTC).toLocaleString()})
                        </span></>
                      )}
                    </MDTypography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="body2" color="text">
                    <b>Capacity:</b> {vehicle.capacity}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Year:</b> {vehicle.year}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Make:</b> {vehicle.make}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Color:</b> {vehicle.color}
                  </MDTypography>

                  <MDTypography variant="body2" color="text">
                    <b>Cell Number:</b> {vehicle.cellNumber}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>
            <Tabs sx={{ mt: 2 }} value={activeTab} onChange={handleChange} aria-label="basic tabs example">
              <Tab
                className={activeTab == 0 ? "tab tab-active" : "tab"}
                label="Details"
                id="details-tab"
                aria-controls="details-tab-panel"
              />
              <Tab
                className={activeTab == 1 ? "tab tab-active" : "tab"}
                label="Trips"
                id="trips-tab"
                aria-controls="trips-tab-panel"
              />
              <Tab
                className={activeTab == 2 ? "tab tab-active" : "tab"}
                label="Modifications"
                id="modifications-tab"
                aria-controls="modifications-tab-panel"
              />
            </Tabs>
            <div id="details-tab-panel" hidden={activeTab !== 0}>
              <MDBox component="form" pb={2} px={2}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={vehicle.isActive}
                          onChange={(e) =>
                            setVehicle({
                              ...vehicle,
                              isActive: !!e.target.checked,
                            })
                          }
                        />
                      }
                      label="Is Active"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={vehicle.isSuspended}
                          onChange={(e) =>
                            setVehicle({
                              ...vehicle,
                              isSuspended: !!e.target.checked,
                            })
                          }
                        />
                      }
                      label="Is Suspended"
                    />
                    <TextField
                      label="Comments"
                      value={vehicle.comments}
                      onChange={(e) =>
                        setVehicle({
                          ...vehicle,
                          comments: e.target.value,
                        })
                      }
                      fullWidth
                      multiline
                      rows={3}
                      margin="normal"
                    />
                    <FormControl sx={{ mt: 2, minWidth: 120 }} fullWidth>
                      <InputLabel shrink id="leaseCompanyId">Lease Company</InputLabel>
                      <Select
                        id="leaseCompanyId"
                        name="leaseCompanyId"
                        labelId="leaseCompanyId"
                        label="Lease Company"
                        notched
                        sx={{ minWidth: 210 }}
                        value={vehicle.leaseCompanyId ?? ""}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            leaseCompanyId: e.target.value === "" ? null : +e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {leaseCompanies
                          .filter((lc) => lc.isActive || lc.id === vehicle.leaseCompanyId)
                          .map((lc) => (
                            <MenuItem key={lc.id} value={lc.id}>
                              {lc.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl sx={{ mt: 2, minWidth: 120 }} fullWidth>
                      <InputLabel id="hubId">Hub</InputLabel>
                      <Select
                        id="hubId"
                        name="hubId"
                        labelId="hubId"
                        label="hub"
                        sx={{ minWidth: 210 }}
                        value={vehicle.hubId}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            hubId: +e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        {hubs &&
                          hubs.map((h, index) => {
                            return (
                              <MenuItem key={index} value={h.id}>
                                {h.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ mt: 2, minWidth: 120 }} fullWidth>
                      <InputLabel id="typeId">Type</InputLabel>
                      <Select
                        id="typeId"
                        name="typeId"
                        labelId="typeId"
                        label="type"
                        sx={{ minWidth: 210 }}
                        value={vehicle.typeId}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            typeId: +e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        {types &&
                          types.map((t, index) => {
                            return (
                              <MenuItem key={index} value={t.id}>
                                {t.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ mt: 2, minWidth: 120 }} fullWidth>
                      <InputLabel id="statusId">Status</InputLabel>
                      <Select
                        id="statusId"
                        name="statusId"
                        labelId="statusId"
                        label="Status"
                        sx={{ minWidth: 210 }}
                        value={vehicle.statusId}
                        onChange={(e) => setVehicle({ ...vehicle, statusId: e.target.value })}
                        displayEmpty
                      >
                        {statuses &&
                          statuses.map((s, index) => {
                            return (
                              <MenuItem key={index} value={s.id}>
                                {s.name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    {displayOutOfServiceReason && (
                      <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }} fullWidth>
                        <InputLabel id="outofServiceReasonId">Out of Service Reason</InputLabel>
                        <Select
                          id="outofServiceReasonId"
                          name="outofServiceReasonId"
                          labelId="outofServiceReasonId"
                          label="Out of Service Reason"
                          sx={{ minWidth: 210 }}
                          value={vehicle.outOfServiceReasonId}
                          onChange={(e) =>
                            setVehicle({
                              ...vehicle,
                              outOfServiceReasonId: +e.target.value,
                            })
                          }
                          displayEmpty
                        >
                          {outOfServiceReasons &&
                            outOfServiceReasons.map((s, index) => {
                              return (
                                <MenuItem key={index} value={s.id}>
                                  {s.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              </MDBox>

              <Grid item xs={12} mt={2}>
                <MDBox p={3}>
                  <MDTypography variant="h5">Device/Modem Details</MDTypography>
                </MDBox>
                <MDBox component="form" pb={3} px={3}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Modem SIM Number"
                        value={vehicle.modemSimNumber}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            modemSimNumber: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                      />
                      <TextField
                        label="Modem ESN Number"
                        value={vehicle.modemEsnNumber}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            modemEsnNumber: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Camera Device Number"
                        value={vehicle.cameraDeviceNumber}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            cameraDeviceNumber: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                      />
                      <TextField
                        label="Device Serial Number"
                        value={vehicle.deviceSerialNumber}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            deviceSerialNumber: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                </MDBox>
                <Grid item xs={12} justifyItems="space-between">
                  <Grid container spacing={2} sx={{ pt: 2, pb: 2, pr: 4 }}>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                      <MDButton variant="text" color="secondary" to="/vehicles" component={RouterLink}>
                        Cancel
                      </MDButton>
                      <MDButton variant="contained" color="dark" onClick={save}>
                        Save
                      </MDButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
            <div id="trips-tab-panel" hidden={activeTab !== 1}>
              <VehicleTrips unitNumber={vehicle.unitNumber}></VehicleTrips>
            </div>
            <div id="modifications-tab-panel" hidden={activeTab !== 2}>
              {activeTab === 2 && (
                <VehicleHistory
                  vehicleId={vehicle.id}
                  hubs={hubs ?? []}
                  types={types ?? []}
                  statuses={statuses ?? []}
                  outOfServiceReasons={outOfServiceReasons ?? []}
                />
              )}
            </div>
          </Card>
        </>
      )}
    </>
  );
};

export default VehicleEdit;
